import ColorPicker from "components/basic/color-picker/ColorPicker";
import DatasetTitle from "components/basic/display-titles/DatasetTitle";
import { DatasetDropdown } from "components/basic/dropdowns/dataset/DatasetDropdown";
import {
  IDatasetSetpointConfig,
  IDatasetSetpointValueProp,
} from "components/dashboard/dataset-setpoint-table/common/api/interfaces/interfaces";
import { Button } from "primereact/button";
import { Column, ColumnEditorOptions } from "primereact/column";
import { DataTable, DataTableRowEditCompleteEvent } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";
import React, { lazy, Suspense, useState } from "react";
import { T } from "services/lang/LanguageService";
import { handleRowReorderEvt } from "utils/utils";

const ValuesValidationEditor = lazy(() => import("../values-validation-editor/ValuesValidationEditor"));

interface IDatasetSetpointValuesEditorProps {
  currentConfig: IDatasetSetpointConfig;
  setCurrentConfig: (config: IDatasetSetpointConfig) => void;
}

type IndexedDatasetSetpointValue = IDatasetSetpointValueProp & { index: number };

enum DefaultHighlightColor {
  defaultTextColor = "#4b5563",
  defaultBackgroundColor = "#FFFFFF",
}

const DatasetSetpointValuesEditor = ({
  currentConfig,
  setCurrentConfig,
}: IDatasetSetpointValuesEditorProps) => {
  const indexedValues = structuredClone(currentConfig).values.map((item, index) => ({
    ...item,
    index,
  }));

  const [isValidationDialogVisible, setValidationDialogVisibility] = useState<boolean>(false);
  const [selectedValue, setSelectedValue] = useState<any>(undefined);

  const handleShowValidationsClicked = (rowData: any) => {
    setSelectedValue(rowData);
    setValidationDialogVisibility(true);
  };

  const handleAddValue = () => {
    const tempConfig = structuredClone(currentConfig);
    const newValue = { dataset: "", setpointDataset: "", name: "", tag: "" };
    tempConfig.values.push(newValue);
    setCurrentConfig(tempConfig);
  };

  const handleAddSeparator = () => {
    const tempConfig = structuredClone(currentConfig);
    const newValue = { name: " " };
    tempConfig.values.push(newValue);
    setCurrentConfig(tempConfig);
  };

  const onRowEditComplete = (e: DataTableRowEditCompleteEvent) => {
    const updatedValues = [...currentConfig.values];
    const { newData, index } = e;
    updatedValues[index] = newData;
    setCurrentConfig({ ...currentConfig, values: updatedValues });
  };

  const tableHeader = (
    <div className="flex justify-content-end gap-1">
      <Button
        label={T("action.add-values")}
        icon="pi pi-plus"
        className="dashboard-editor-button icon-green"
        onClick={handleAddValue}
      />
      <Button
        label={T("action.add-separator")}
        icon="pi pi-plus"
        className="dashboard-editor-button"
        onClick={handleAddSeparator}
      />
    </div>
  );

  const tagBodyTemplate = (options: ColumnEditorOptions) => {
    return (
      <InputText
        value={options.rowData.tag}
        className="w-full"
        onChange={e => options.editorCallback?.(e.target.value)}
      />
    );
  };

  const datasetEditorTemplate = (options: ColumnEditorOptions) => {
    return (
      <DatasetDropdown
        value={options.rowData.dataset}
        onChange={e => options.editorCallback?.(e ?? undefined)}
      />
    );
  };

  const datasetBodyTemplate = (rowData: IndexedDatasetSetpointValue) => {
    if (rowData.dataset === undefined || rowData.dataset.length === 0) {
      return <span />;
    }
    return <DatasetTitle identifier={rowData.dataset} wordBreak />;
  };

  const nameBodyTemplate = (options: ColumnEditorOptions) => {
    return (
      <InputText
        value={options.rowData.name}
        className="w-full"
        onChange={e => options.editorCallback?.(e.target.value)}
      />
    );
  };

  const setpointDatasetEditorTemplate = (options: ColumnEditorOptions) => {
    return (
      <DatasetDropdown value={options.rowData.setpointDataset} onChange={e => options.editorCallback?.(e)} />
    );
  };

  const setpointDatasetBodyTemplate = (rowData: IndexedDatasetSetpointValue) => {
    if (rowData.setpointDataset == null || rowData.setpointDataset.length === 0) {
      return <span />;
    }
    return (
      <div className="w-full">
        <DatasetTitle identifier={rowData.setpointDataset} wordBreak />
      </div>
    );
  };

  const defaultHighlightEditorTemplate = (options: ColumnEditorOptions) => {
    return (
      <div className="flex justify-content-center align-items-center">
        <ColorPicker
          value={options.rowData.highlightColor?.color || DefaultHighlightColor.defaultTextColor}
          onValueChange={e =>
            options.editorCallback?.({ ...options.rowData.highlightColor, color: e.target.value })
          }
        />
        <ColorPicker
          value={options.rowData.highlightColor?.bgColor || DefaultHighlightColor.defaultBackgroundColor}
          onValueChange={e =>
            options.editorCallback?.({ ...options.rowData.highlightColor, bgColor: e.target.value })
          }
        />
        <i
          className="pi pi-refresh ml-2 cursor-pointer"
          onClick={() => options.editorCallback?.(undefined)}
        />
      </div>
    );
  };

  const defaultHighlightBodyTemplate = (rowData: IndexedDatasetSetpointValue) => {
    if (rowData.name === " ") {
      return <span />;
    }
    let tempColor: string = DefaultHighlightColor.defaultTextColor;
    let tempBgColor: string = DefaultHighlightColor.defaultBackgroundColor;

    if (rowData.highlightColor) {
      tempColor = rowData.highlightColor.color?.startsWith("#")
        ? rowData.highlightColor.color
        : `#${rowData.highlightColor.color}`;
      tempBgColor = rowData.highlightColor.bgColor?.startsWith("#")
        ? rowData.highlightColor.bgColor
        : `#${rowData.highlightColor.bgColor}`;
    }
    return (
      <span className="p-1 pl-2 pr-2" style={{ color: tempColor, backgroundColor: tempBgColor }}>
        Text
      </span>
    );
  };

  const validationBodyTemplate = (rowData: IndexedDatasetSetpointValue) => {
    if (rowData.name === " ") {
      return <span />;
    }
    return (
      <Button
        label="Show"
        className="p-button-sm p-button-text p-button-outlined"
        onClick={() => handleShowValidationsClicked(rowData)}
        disabled={
          rowData.dataset === undefined ||
          rowData.dataset.length === 0 ||
          rowData.setpointDataset == null ||
          rowData.setpointDataset.length === 0
        }
      />
    );
  };

  const deleteValueButtonBodyTemplate = (rowData: IndexedDatasetSetpointValue) => {
    return (
      <i
        className="pi pi-trash text-red-500 cursor-pointer"
        onClick={() => {
          const tempConfig = structuredClone(currentConfig);
          tempConfig.values.splice(rowData.index, 1);
          setCurrentConfig(tempConfig);
        }}
      />
    );
  };

  return (
    <>
      <DataTable
        value={indexedValues}
        editMode="row"
        onRowEditComplete={onRowEditComplete}
        tableStyle={{ width: "100%" }}
        onRowReorder={handleRowReorderEvt(currentConfig.values, setCurrentConfig, currentConfig, "values")}
        size="small"
        reorderableRows
        header={tableHeader}
      >
        <Column rowReorder style={{ width: "3%" }} />
        <Column field="tag" header={T("word.tag")} style={{ width: "5%" }} editor={tagBodyTemplate} />
        <Column
          field="dataset"
          header={T("word.dataset")}
          style={{ minWidth: "22%" }}
          editor={datasetEditorTemplate}
          body={datasetBodyTemplate}
        />
        <Column
          field="name"
          header={T("word.dataset-name")}
          style={{ width: "20%" }}
          editor={nameBodyTemplate}
        />
        <Column
          field="setpointDataset"
          header={T("word.setpoint-dataset")}
          style={{ width: "20%" }}
          editor={setpointDatasetEditorTemplate}
          body={setpointDatasetBodyTemplate}
        />
        <Column
          field="highlightColor"
          header={T("word.default-highlight")}
          editor={defaultHighlightEditorTemplate}
          body={defaultHighlightBodyTemplate}
          align="center"
          style={{ width: "10%" }}
        />
        <Column
          field="validation"
          header={T("word.validation")}
          align="center"
          style={{ width: "6%" }}
          body={validationBodyTemplate}
        />
        <Column rowEditor align="center" className="p-0 w-6rem" />
        <Column style={{ width: "2%" }} align="center" body={deleteValueButtonBodyTemplate} />
      </DataTable>
      <Dialog
        header={T("word.validation")}
        visible={isValidationDialogVisible}
        style={{ width: "30vw" }}
        onHide={() => setValidationDialogVisibility(false)}
      >
        <Suspense fallback={<ProgressSpinner />}>
          <ValuesValidationEditor
            selectedValue={selectedValue}
            currentConfig={currentConfig}
            setCurrentConfig={setCurrentConfig}
          />
        </Suspense>
      </Dialog>
    </>
  );
};

export default DatasetSetpointValuesEditor;
